import React, { useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import MultiAddTable from '../../../components/table/MultiAddTable';
import {
  BusinessReportAddRow,
  EmptyBusinessReportAddRow,
} from '../../../types/businessreport/BusinessReportAddRow';
import { BUSINESS_REPORT_HEADER } from '../../../constants/BusinessReport';
import { v4 as uuidv4 } from 'uuid';
import AddBusinessReportRow from './AddBusinessReportRow';
import { format } from 'date-fns';
import AddButton from '../../../components/button/AddButton';
import MultiSaveButton from '../../../components/button/MultiSaveButton';
import { Col, Row } from 'react-bootstrap';
import YmdPicker from '../../../components/picker/YmdPicker';
import TableBaseButton from '../../../components/button/TableBaseButton';
import ManHoursCategoryDescription from '../../../components/businessreport/ManhoursCategoryDescription';
import { BusinessReportPostRequest } from '../../../types/businessreport/BusinessReportPostRequest';
import useValidation from '../../../hooks/useValidation';
import { BUSINESS_REPORT_VALIDATION } from '../../../constants/validation/reportValidation';
import { useMutation } from '@tanstack/react-query';
import {
  postBusinessReports,
  postBusinessReportsBulk,
} from '../../../services/api';
import LoadingOverlay from '../../../components/LoadingOverlay';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/BusinessReportHeader.css';
import { Axios, AxiosError } from 'axios';

const BusinessReport = () => {
  // 業務報告の日付選択用
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  // pickerで選択された日付を一時保存するためのstate
  const [pickedSelectedDate, setPickedSelectedDate] = useState<Date | null>(
    selectedDate
  );
  // 個別保存、追加のため、uuidを使用して行にIDを付与する
  const [businessReports, setBusinessReports] = useState<
    BusinessReportAddRow[]
  >([
    {
      ...EmptyBusinessReportAddRow,
      id: uuidv4(),
      date: selectedDate ? format(selectedDate, 'yyyy/MM/dd') : '',
    },
  ]);
  const [manHourTotal, setManHourTotal] = useState<string>('');
  const { validate } = useValidation(BUSINESS_REPORT_VALIDATION);

  // 表の横スクロール要素（ヘッダー追従用）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  const singleRowPostRequest = useMutation({
    mutationFn: (data: BusinessReportPostRequest) => postBusinessReports(data),
    onSuccess: () => {
      alert('業務報告を送信しました');
    },
    onError: (error) => {
      alert('業務報告の送信に失敗しました');
    },
  });

  const multipleRowPostRequest = useMutation({
    mutationFn: (data: BusinessReportPostRequest[]) =>
      postBusinessReportsBulk(data),
    onSuccess: () => {
      alert('業務報告を一括送信しました');
    },
    onError: (error: AxiosError) => {
      alert('業務報告の一括送信に失敗しました');
    },
  });

  const handleAddRow = () => {
    const newRow: BusinessReportAddRow = {
      ...EmptyBusinessReportAddRow,
      id: uuidv4(),
      date: selectedDate ? format(selectedDate, 'yyyy/MM/dd') : '',
    };
    setBusinessReports([...businessReports, newRow]);
  };

  const resetRows = () => {
    setBusinessReports([
      {
        ...EmptyBusinessReportAddRow,
        id: uuidv4(),
        date: selectedDate ? format(selectedDate, 'yyyy/MM/dd') : '',
      },
    ]);
    setManHourTotal('');
  };

  const handleCancelButtonClick = (itemId: string) => {
    const newRows = businessReports.filter((item) => item.id !== itemId);
    const total = newRows.reduce((acc, item) => acc + item.manHoursTotal, 0);
    setManHourTotal(total.toString());
    setBusinessReports(newRows);
  };

  const handleSaveButtonClick = (itemId: string) => {
    const itemToSave = businessReports.find((item) => item.id === itemId);
    if (!itemToSave) return;

    const payload: BusinessReportPostRequest = toPostRequest(itemToSave);
    const validationError = validate(payload);
    if (validationError) {
      alert(validationError);
      return;
    }
    singleRowPostRequest.mutate(payload);

    const newRows = businessReports.filter((item) => item.id !== itemId);
    const total = newRows.reduce((acc, item) => acc + item.manHoursTotal, 0);

    // 追加行がなくなったら初期状態に戻す
    if (newRows.length === 0) {
      resetRows();
      return;
    }
    setBusinessReports(newRows);
    setManHourTotal(total.toString());
  };

  const handleAllSaveButtonClick = () => {
    const payload: BusinessReportPostRequest[] = businessReports.map((item) =>
      toPostRequest(item)
    );
    for (const item of payload) {
      const validationError = validate(item);
      if (validationError) {
        alert(validationError);
        return;
      }
    }
    multipleRowPostRequest.mutate(payload);
    resetRows();
  };

  const toPostRequest = (
    item: BusinessReportAddRow
  ): BusinessReportPostRequest => {
    const { id, manHoursCategory, ...rest } = item;

    const categories = ['A', 'B', 'C', 'D', 'E'] as const;
    const flags = categories.reduce(
      (acc, cat) => ({
        ...acc,
        [`manHours${cat}`]: manHoursCategory === cat,
      }),
      {} as Record<string, boolean>
    );

    return { ...rest, ...flags };
  };

  const handleAddItemChange = (
    itemId: string,
    key: keyof BusinessReportAddRow,
    value: string | boolean | number
  ) => {
    const updatedItems = businessReports.map((item) =>
      item.id === itemId ? { ...item, [key]: value } : item
    );

    if (key === 'manHoursTotal') {
      const total = updatedItems.reduce((acc, i) => acc + i.manHoursTotal, 0);
      setManHourTotal(total.toString());
    }
    setBusinessReports(updatedItems);
  };

  const handleSelectDateChange = (date: Date | null) => {
    setSelectedDate(date);
    setBusinessReports((rows) =>
      rows.map((item) => ({
        ...item,
        date: date ? format(date, 'yyyy/MM/dd') : '',
      }))
    );
  };

  const isMutating = [singleRowPostRequest, multipleRowPostRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer title="業務報告入力">
      <LoadingOverlay isMutating={isMutating} />

      {/* ヘッダー：共通CSS（header-responsive.css）に合わせた構造 */}
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-1"
        outerRef={headerRef}
      >
        <Row className="header-row-businessreport gx-0">
          {/* 左：カテゴリ説明（可変領域／縮めない） */}
          <Col className="header-fields-businessreport order-1">
            <ManHoursCategoryDescription />
          </Col>

          {/* 右：ボタン群（共通CSSに準拠） */}
          <Col xs="auto" className="header-right-businessreport order-2">
            <div className="actions-group-businessreport">
              {/* 1行目固定（検索・リセット相当の“変えない”塊） */}
              <div className="actions-searchreset-businessreport">
                <YmdPicker
                  selectedDate={pickedSelectedDate}
                  setSelectedDate={setPickedSelectedDate}
                />
                <TableBaseButton
                  buttonText="この日付で設定"
                  className="btn btn-add-custom"
                  onClick={() => handleSelectDateChange(pickedSelectedDate)}
                />
              </div>

              {/* 折り返し対象（幅が狭いと2行目に落ちる） */}
              <div className="actions-extra-businessreport mt-2">
                <MultiSaveButton onClick={handleAllSaveButtonClick} />
                <AddButton onClick={handleAddRow} buttonLabel="業務報告" />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      {/* 表本体（横スクロールはここだけ） */}
      <MultiAddTable<BusinessReportAddRow>
        headers={BUSINESS_REPORT_HEADER}
        items={businessReports}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
        addItem={(item) => (
          <AddBusinessReportRow
            item={item}
            onChange={handleAddItemChange}
            onSubmit={handleSaveButtonClick}
            onCancel={handleCancelButtonClick}
          />
        )}
      />

      {/* 合計（必要なら右端追従させるため 2つ目の SyncedScrollHeader を利用） */}
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mt-2"
        outerRef={footerRef}
      >
        <Row className="gx-0">
          <Col className="flex-grow-1" />
          <Col
            xs="auto"
            className="d-flex justify-content-end align-items-center gap-3"
          >
            <div className="me-4">稼働時間合計: {manHourTotal}</div>
          </Col>
        </Row>
      </SyncedScrollHeader>
    </PageContainer>
  );
};

export default BusinessReport;
